import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Cancel } from "@mui/icons-material";

interface InputImageCellProps {
  id?: string;
  label?: string;
  imageUrl?: string | null;
  onFileChange?: (file: File | null) => void;
  accept?: string;
  required?: boolean;
  className?: string;
}

const InputImageCell: React.FC<InputImageCellProps> = ({
  id,
  label,
  imageUrl = null,
  onFileChange,
  accept = "image/*",
  required = false,
  className = "",
}) => {
  const [preview, setPreview] = useState<string | null>(imageUrl);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setPreview(imageUrl);
  }, [imageUrl]);

  useEffect(() => {
    return () => {
      // revoke object URL if used
      if (preview && preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handlePick = () => {
    fileRef.current?.click();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    if (file) {
      const url = URL.createObjectURL(file);
      setPreview(url);
      onFileChange && onFileChange(file);
    } else {
      setPreview(null);
      onFileChange && onFileChange(null);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setPreview(null);
    onFileChange && onFileChange(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <Box className={className}>
      {label && (
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
        </Typography>
      )}

      <Box
        sx={{
          width: 96,
          height: 96,
          borderRadius: 1,

          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          cursor: "pointer",
          position: "relative",
        }}
        onClick={handlePick}
      >
        {preview ? (
          <>
            <img
              src={preview}
              alt="preview"
              style={{
                borderRadius: 1,
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />

            <Cancel
              onClick={handleRemove}
              fontSize="small"
              color="error"
              sx={{
                position: "absolute",
                top: -8,
                right: -8,
              }}
            />
          </>
        ) : (
          <Box
            display="flex"
            width="100%"
            height="100%"
            alignItems="center"
            justifyContent="center"
            sx={{ color: "text.disabled" }}
          >
            No image
          </Box>
        )}
      </Box>

      <input
        ref={fileRef}
        id={id}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={{ display: "none" }}
      />
    </Box>
  );
};

export default InputImageCell;
